import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { 
  Briefcase, 
  UserCheck, 
  Search, 
  ShieldCheck, 
  Zap, 
  Calendar,
  Users,
  ArrowRight,
  Linkedin,
  Building2,
  Quote,
  Menu,
  X,
  Sparkles,
  Loader2
} from 'lucide-react';

import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const HERO_IMAGE = "https://images.unsplash.com/photo-1758873268631-fa944fc5cad2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBjb2xsYWJvcmF0aW9uJTIwbW9kZXJuJTIwb2ZmaWNlJTIwdGVhbXxlbnwwfHx8fDE3NzQwMDc2MzB8MA&ixlib=rb-4.1.0&q=85&w=1200";

const testimonials = [
  {
    quote: "We faced challenges in finding a suitable consultant in our area due to the niche nature of our segment. Additionally, collaborating with central government institutions proved difficult for a startup because of bureaucratic hurdles. However, Physics Mindboggler simplified the process for us. Within just a few days, we were able to identify an expert with the relevant experience we needed.",
    name: "Dr. Umesh Vasu",
    role: "CEO at Tharam-Thiran Green Energy Flow"
  },
  {
    quote: "Physics Mindboggler has been a tremendous help in connecting us with the right person for our project. They took the time to understand our requirements and made a lot of effort to ensure we found the perfect match. Even found another person when the initial contact wasn't able to deliver. Also helped us with the formalities. What sets them apart is their dedication. They didn't just provide contacts, but stayed with us throughout the process, coordinating seamlessly with researchers, freelancers, and industry professionals. They have a deep understanding of the system. We're grateful for the efforts and highly recommend them to any startup looking for meaningful connections.",
    name: "Puru Kumar",
    role: "Co-Founder & CTO at Apeiro Energy"
  },
  {
    quote: "If we hadn't connected with Physics Mindboggler (PM), it could have significantly delayed or even altered the development of our product. By helping us meet the right engineers, they facilitated critical collaborations that were essential for the technical aspects of our product design. Without this connection, we would have faced challenges in identifying the right talent, possibly leading to increased development time, higher costs, and a longer time-to-market. Team PM's assistance streamlined the process, ensuring we could focus on other key business areas while the product development progressed efficiently.",
    name: "Soham Mhatre",
    role: "Co-Founder of MedFassst"
  },
  {
    quote: "Always a pleasure working with Physics Mindboggler (PM). Team PM is patient and understood my needs and connected me to appropriate persons from RnD and Manufacturing. They give personal attention wherever the work gets stuck with the connections. PM has made project development collaborations feasible for me.",
    name: "Rajat Jain",
    role: "Founder of Smartivity"
  },
  {
    quote: "Physics Mindboggler (PM) is a unique venture harnessing education, research, and entrepreneurship. PM connected us with a startup in our research area, greatly benefiting our hyper-focused product development. Special thanks to Team PM for forging this relationship. Ventures like PM require a specific mindset, and Team PM epitomizes it. Gratitude to the Universe!",
    name: "Ajit Padmanabh",
    role: "Founder of WHO VR"
  },
  {
    quote: "Physics Mindboggler (PM) is one of the most competent and well-networked organizations. Even for niche consultation needs, Team PM connected me with the right specialists. Their seamless networking makes it easy to trust and begin work without delay. I highly recommend any startup or organization to contact PM for their needs; rest assured, they will connect you to the best professionals.",
    name: "Chaitanya Doshi",
    role: "Founder of Kore AMMR"
  },
  {
    quote: "Physics Mindboggler (PM) played a crucial role in connecting Biokraft Foods with an Expert. Their efficient mediation led to a highly productive partnership that has significantly advanced our market research efforts. We are grateful for their invaluable support.",
    name: "Kamalnayan Tibrewal",
    role: "CEO of Biokraft Foods"
  },
  {
    quote: "Physics Mindboggler (PM) is like an ocean of opportunities for any individual innovator or startup for product development. Even though I had a prototype, it was PM who guided and paved the way to connect me with the right manufacturing partner. Based on the manufacturing partner's past work shared by PM, made me confident to trust PM and work with the service provider. Thank you PM :)",
    name: "Rahul Nambiar",
    role: "Founder of Aurtus"
  }
];

const features = [
  {
    icon: UserCheck,
    title: "Verified Experts",
    description: "Only qualified experts with lab access and industry experience are visible."
  },
  {
    icon: Search,
    title: "Smart Matching",
    description: "AI-driven matchmaking finds the perfect expert for your needs."
  },
  {
    icon: ShieldCheck,
    title: "Quality Assured",
    description: "Three-point verification ensures only committed professionals join."
  },
  {
    icon: Calendar,
    title: "Easy Scheduling",
    description: "Book meetings directly through integrated Calendly scheduling."
  },
  {
    icon: Zap,
    title: "Instant Notifications",
    description: "Experts receive real-time alerts when industry shows interest."
  },
  {
    icon: Users,
    title: "Growing Network",
    description: "Connect with a expanding community of verified researchers and innovators."
  }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isIndustry, token, API } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submitPrompt = async () => {
    const trimmed = prompt.trim();
    if (trimmed.length < 20) {
      toast.error('Please describe your requirement in at least a couple of sentences.');
      return;
    }
    if (isAuthenticated && isIndustry) {
      // Logged in as industry — create project immediately
      setSubmitting(true);
      try {
        const res = await fetch(`${API}/projects`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ prompt: trimmed })
        });
        if (res.ok) {
          const data = await res.json();
          navigate(`/industry/projects/${data.id}`);
        } else {
          toast.error('Could not create project');
        }
      } finally {
        setSubmitting(false);
      }
    } else {
      // Not authenticated — stash prompt and redirect to signup/login
      try {
        sessionStorage.setItem('pending_project_prompt', trimmed);
      } catch (e) { /* noop */ }
      navigate('/industry/auth?next=create_project');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="nav-header relative">
        <div className="container-main py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md gradient-primary flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-xl font-semibold tracking-tight" data-testid="logo-text">CollabHive</span>
              <span className="text-[10px] text-muted-foreground mt-0.5">Powered by PM</span>
            </div>
          </Link>
          
          {/* Desktop nav — unchanged */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/industry/prompt">
              <Button variant="ghost" data-testid="nav-search-btn">Start a Project</Button>
            </Link>
            <Link to="/expert/signup">
              <Button variant="outline" data-testid="nav-expert-btn">I'm an Expert</Button>
            </Link>
            <Link to="/industry/auth">
              <Button data-testid="nav-industry-btn">Industry Login</Button>
            </Link>
          </div>

          {/* Mobile hamburger trigger */}
          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-border bg-background hover:bg-muted transition-colors"
            onClick={() => setMobileMenuOpen(v => !v)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-background border-b border-border shadow-lg z-50 animate-fade-in">
            <div className="container-main py-4 flex flex-col gap-3">
              <Link to="/industry/prompt" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start gap-2" data-testid="mobile-nav-search-btn">
                  <Search className="w-4 h-4" />
                  Start a Project
                </Button>
              </Link>
              <Link to="/expert/signup" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="outline" className="w-full justify-start gap-2" data-testid="mobile-nav-expert-btn">
                  <Linkedin className="w-4 h-4" />
                  I'm an Expert
                </Button>
              </Link>
              <Link to="/industry/auth" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full justify-start gap-2" data-testid="mobile-nav-industry-btn">
                  <Building2 className="w-4 h-4" />
                  Industry Login
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-dark hero-pattern">
        <div className="container-main py-20 md:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
                <Zap className="w-4 h-4" />
                <span>Connecting Expertise with Industry</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Find the Right <span className="text-primary">Expert</span> for Your Challenge
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground max-w-xl">
                CollabHive bridges the gap between verified scientists/engineers and industry. 
                Discover, connect, and collaborate with precision.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/industry/prompt">
                  <Button size="lg" className="w-full sm:w-auto gap-2" data-testid="hero-search-btn">
                    <Sparkles className="w-5 h-5" />
                    Start a Project
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/expert/signup">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto gap-2 border-white/20 text-white hover:bg-white/10" data-testid="hero-expert-btn">
                    <Linkedin className="w-5 h-5" />
                    Join as Expert
                  </Button>
                </Link>
              </div>
              
              <div className="flex items-center gap-8 pt-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-primary" />
                  <span>Verified Profiles</span>
                </div>
                <div className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-primary" />
                  <span>Industry Ready</span>
                </div>
              </div>
            </div>
            
            <div className="relative animate-slide-up stagger-2 hidden lg:block">
              <div className="absolute -inset-4 bg-primary/20 rounded-2xl blur-3xl" />
              <img 
                src={HERO_IMAGE} 
                alt="Collaboration" 
                className="relative rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section bg-muted/30">
        <div className="container-main">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How CollabHive Works</h2>
            <p className="text-muted-foreground">
              A streamlined process to connect industry with academia
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* For Experts */}
            <Card className="card-hover">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-[#0077b5]/10 flex items-center justify-center mx-auto mb-6">
                  <Linkedin className="w-8 h-8 text-[#0077b5]" />
                </div>
                <h3 className="text-xl font-semibold mb-3">1. Experts Sign Up</h3>
                <p className="text-muted-foreground">
                  Sign up with LinkedIn, provide your ORCID ID, and complete a quick qualification check. 
                  Only verified experts are visible to industry.
                </p>
              </CardContent>
            </Card>
            
            {/* For Industry */}
            <Card className="card-hover">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <Search className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">2. Industry Searches</h3>
                <p className="text-muted-foreground">
                  Describe your problem or expertise needed. Our AI matchmaking engine connects you with the most relevant experts.
                </p>
              </CardContent>
            </Card>
            
            {/* Connect */}
            <Card className="card-hover">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <Calendar className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">3. Schedule & Connect</h3>
                <p className="text-muted-foreground">
                  Express interest in an expert, and we'll facilitate the connection. 
                  Book a meeting directly through Calendly.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="section">
        <div className="container-main">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Choose CollabHive</h2>
            <p className="text-muted-foreground">
              Built for meaningful collaboration between academia and industry
            </p>
          </div>
          
          <div className="bento-grid">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="feature-card animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="feature-card-icon">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section bg-muted/30">
        <div className="container-main">
          <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              <Quote className="w-4 h-4" />
              <span>What People Say</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Trusted by Innovators &amp; Researchers
            </h2>
            <p className="text-muted-foreground">
              Voices from founders and leaders who built breakthroughs with the help of Physics Mindboggler.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {testimonials.map((t, index) => (
              <Card
                key={index}
                className="card-hover animate-fade-in border-l-4 border-l-primary/60"
                style={{ animationDelay: `${index * 0.06}s` }}
                data-testid={`testimonial-${index}`}
              >
                <CardContent className="p-6 sm:p-8">
                  <Quote className="w-7 h-7 text-primary/40 mb-4" />
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6 whitespace-pre-line">
                    {t.quote}
                  </p>
                  <div className="pt-4 border-t">
                    <p className="font-semibold text-foreground">{t.name}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1">{t.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="hero-dark">
        <div className="container-main py-20">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Expert CTA */}
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-8">
                <div className="w-12 h-12 rounded-lg bg-[#0077b5] flex items-center justify-center mb-6">
                  <UserCheck className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-3">Are You an Expert?</h3>
                <p className="text-white/70 mb-6">
                  Join our network of verified researchers and consultants. 
                  Get discovered by industry leaders seeking your expertise.
                </p>
                <Link to="/expert/signup">
                  <Button className="btn-linkedin gap-2 w-full sm:w-auto" data-testid="cta-expert-btn">
                    <Linkedin className="w-5 h-5" />
                    Sign Up with LinkedIn
                  </Button>
                </Link>
              </CardContent>
            </Card>
            
            {/* Industry CTA */}
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-8">
                <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center mb-6">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-3">Looking for Expertise?</h3>
                <p className="text-white/70 mb-6">
                  Find the perfect expert for your R&D challenges, consulting needs, 
                  or collaborative projects.
                </p>
                <Link to="/industry/prompt">
                  <Button className="gap-2 w-full sm:w-auto" data-testid="cta-industry-btn">
                    <Search className="w-5 h-5" />
                    Start Searching
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container-main">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md gradient-primary flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div className="flex flex-col leading-none">
                <span className="text-lg font-semibold">CollabHive</span>
                <span className="text-[10px] text-muted-foreground mt-0.5">Powered by PM</span>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground">
              Connecting Industry with Academia
            </p>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/industry/prompt" className="hover:text-foreground transition-colors">Start a Project</Link>
              <Link to="/expert/signup" className="hover:text-foreground transition-colors">Join as Expert</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
